import React, { useCallback, useState } from "react";
import { Table, Button, Space, Tag, message, Popconfirm, Image, Modal, Input, Typography } from 'antd'
import { PlusOutlined, CopyOutlined, LinkOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useApiSelector } from 'src/hooks'
import { useRequest, dispatchAsync } from 'friday-async'
import { activityStatusMap } from '../constants'
import CreateDrawer from './CreateDrawer'
import config from 'src/config'
import dayjs from 'dayjs'

const { Text } = Typography;

const List = () => {

  const navigate = useNavigate();

  const [drawerInfo, setDrawerInfo] = React.useState<{visible: boolean; info: any}>({
    visible: false,
    info: null
  })
  const [linkModal, setLinkModal] = useState<{ visible: boolean; code: string }>({
    visible: false, code: ''
  })

  const apis = useApiSelector();

  const { dataArray, isValidating, revalidate  } = useRequest(apis.activity.list(), {
    paginated: false,
  })

  const handleToggleStatus = useCallback(async (record: any) => {
    const newStatus = record.status === 'active' ? 'inactive' : 'active'
    const { error } = await dispatchAsync(apis.activity.toggleStatus({_id: record._id, status: newStatus}))
    if (error) return message.error('操作失败')
    message.success(newStatus === 'active' ? '已启用' : '已下线')
    revalidate()
  }, [apis, revalidate])

  const handleRemove = useCallback(async (record: any) => {
    const { error } = await dispatchAsync(apis.activity.remove({_id: record._id}))
    if (error) return message.error('删除失败')
    message.success('删除成功')
    revalidate()
  }, [apis, revalidate])

  const handleDeliveryLink = useCallback((record: any) => {
    setLinkModal({ visible: true, code: record.code })
  }, [])

  const getDeliveryLink = (code: string) => {
    return `alipays://platformapi/startapp?appId=2021004191661502&page=pages/activity/detail/detail?code=${code}`
  }

  const handleCopyLink = () => {
    const link = getDeliveryLink(linkModal.code)
    navigator.clipboard.writeText(link).then(() => {
      message.success('链接已复制到剪贴板')
    }).catch(() => {
      message.error('复制失败，请手动复制')
    })
  }

  const columns = [
    {
      title: '活动名称',
      dataIndex: 'name',
      key: 'name',
      width: 180,
    },
    {
      title: '活动标识',
      dataIndex: 'code',
      key: 'code',
      width: 140,
      render: (v: string) => v ? <Tag>{v}</Tag> : '-',
    },
    {
      title: '展示图',
      dataIndex: 'displayImage',
      key: 'displayImage',
      width: 100,
      render: (v: string) => v ? (
        <Image
          src={`${config.imgHost}${v.startsWith('/') ? v.slice(1) : v}`}
          alt="展示图"
          width={60}
          height={60}
          style={{ objectFit: 'cover', borderRadius: 4 }}
          preview={{ mask: '预览' }}
        />
      ) : <Tag>-</Tag>,
    },
    {
      title: '权益',
      dataIndex: 'benefit',
      key: 'benefit',
      width: 150,
      ellipsis: true,
      render: (v: any) => v?.name ? `${v.name}` : '-',
    },
    {
      title: '表单字段',
      dataIndex: 'formFields',
      key: 'formFields',
      width: 100,
      render: (v: any[]) => <Tag>{v?.length || 0} 个字段</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render(status: string) {
        const info = activityStatusMap[status] || activityStatusMap.inactive
        return <Tag color={info.color}>{info.label}</Tag>
      }
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 170,
    },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      width: 280,
      fixed: 'right',
      render(_: any, record: any) {
        return (
          <Space>
            <Space>
            <Button
              type="link"
              size="small"
              onClick={() => setDrawerInfo({ visible: true, info: record })}
            >
              编辑
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => navigate(`/activity/leads/${record.code}`)}
            >
              线索管理
            </Button>
            <Button
              type="link"
              size="small"
              icon={<LinkOutlined />}
              onClick={() => handleDeliveryLink(record)}
            >
              投放链接
            </Button>
            <Button
              type="link"
              size="small"
              danger={record.status === 'active'}
              onClick={() => handleToggleStatus(record)}
            >
              {record.status === 'active' ? '下线' : '启用'}
            </Button>
            <Popconfirm title="确定删除该活动？" onConfirm={() => handleRemove(record)}>
              <Button type="link" size="small" danger>删除</Button>
            </Popconfirm>
            </Space>
          </Space>
        )
      }
    }
  ]

  return (
    <Space direction='vertical' style={{width: '100%'}}>
        <Button
            type="primary"
            ghost
            icon={<PlusOutlined />}
            onClick={() => setDrawerInfo({ visible: true, info: null })}
        >
            新建活动
        </Button>
        <Table
          rowKey='_id'
          columns={columns as any}
          dataSource={dataArray}
          loading={isValidating}
          scroll={{ x: 1100 }}
        />
        <CreateDrawer
            info={drawerInfo.info}
            visible={drawerInfo.visible}
            onClose={() => {
                setDrawerInfo({ info: null, visible: false})
                revalidate()
            }}
        />
        {/* 投放链接 Modal */}
        <Modal
          title="投放链接"
          open={linkModal.visible}
          onCancel={() => setLinkModal({ visible: false, code: '' })}
          footer={[
            <Button key="copy" type="primary" icon={<CopyOutlined />} onClick={handleCopyLink}>
              一键复制
            </Button>,
            <Button key="close" onClick={() => setLinkModal({ visible: false, code: '' })}>
              关闭
            </Button>
          ]}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text type="secondary">将以下链接配置到投放渠道，用户点击后直接跳转活动详情页：</Text>
            <Input.TextArea
              value={getDeliveryLink(linkModal.code)}
              readOnly
              rows={3}
              style={{ marginTop: 12, fontFamily: 'monospace' }}
            />
            <Text type="secondary" style={{ fontSize: 12, marginTop: 12, display: 'block' }}>
              注：该链接为支付宝小程序 scheme 链接，可在支付宝内部或外部投放使用
            </Text>
          </Space>
        </Modal>
    </Space>
  )
}

export default List
